/**
 * Step 1: choose a CSV (drop or pick), with what the file needs and a sample.
 */

import { useState } from "react";
import { FileDrop } from "@/components/ui/file-drop";
import { parseCsv, readFileText, type ParsedCsv } from "@/lib/csv";

const MAX_BYTES = 20 * 1024 * 1024;
export const MAX_ROWS = 5000;

export interface LoadedFile {
  name: string;
  size: number;
  csv: ParsedCsv;
}

const SAMPLE = [
  "key,report,patient_age,expected_output,metadata.site",
  'case-1,"CT chest: 7 mm solid nodule in the right upper lobe.",64,nodule,MedStar',
  'case-2,"Chest X-ray: normal study, no acute findings.",41,normal,MedStar',
].join("\n");
const SAMPLE_HREF = `data:text/csv;charset=utf-8,${encodeURIComponent(`${SAMPLE}\n`)}`;

/** The item's slots, then the one special column. */
const NEEDS: Array<[string, string]> = [
  ["Input", "Required, at least one column. What the agent gets: each column is a field under its own name."],
  [
    "Expected output",
    "Optional. The answer you expect. Evaluators that compare with it skip rows without one.",
  ],
  ["Output", "Optional. Answers an agent already gave, to score without running it."],
  ["Metadata", "Optional. Kept with the item, not sent to the agent."],
  ["key", "Optional column. The item's id: a new upload with the same key updates the item."],
];

interface Props {
  onLoad: (f: LoadedFile) => void;
}

export function ImportSource({ onLoad }: Props) {
  const [error, setError] = useState<string | null>(null);

  const read = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_BYTES) return setError("That file is over 20 MB.");
    const csv = parseCsv(await readFileText(file));
    if (csv.header.length === 0) return setError("That file is empty.");
    if (csv.rows.length > MAX_ROWS)
      return setError(`That file has ${csv.rows.length} rows; the limit is ${MAX_ROWS}.`);
    setError(null);
    onLoad({ name: file.name, size: file.size, csv });
  };

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
      <div className="flex flex-col gap-3.5">
        <FileDrop
          id="csv-file"
          accept=".csv,text/csv"
          label="CSV file"
          className="h-[300px]"
          title={
            <span className="text-[17px]">
              Drop a CSV file here, or <span className="text-link">choose one</span>
            </span>
          }
          hint="Up to 20 MB and 5,000 rows · UTF-8 · comma or semicolon separated"
          onFile={(f) => void read(f)}
        />
        {error && (
          <div role="alert" className="rounded-lg bg-err-bg px-3.5 py-3 text-[13px] text-err">
            {error}
          </div>
        )}
      </div>
      <aside className="box flex flex-col gap-3 p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-[15px] font-semibold">What the file needs</h2>
          <a className="btn btn-sm" href={SAMPLE_HREF} download="sample.csv">
            Download sample CSV
          </a>
        </div>
        <p className="text-xs text-muted">
          One row per item, with a header row, and any columns. In the next step you put each column in Input,
          Expected output, Output or Metadata. Columns named{" "}
          <code className="font-mono">expected_output</code>, <code className="font-mono">output</code> or{" "}
          <code className="font-mono">metadata.*</code> are placed for you; the rest start in Input.
        </p>
        <div className="flex flex-col">
          {NEEDS.map(([col, text]) => (
            <div
              key={col}
              className="grid grid-cols-[130px_minmax(0,1fr)] gap-2.5 border-b border-line-soft py-2 text-[13px] last:border-0"
            >
              <span className={col === "key" ? "font-mono text-xs" : "font-medium"}>{col}</span>
              <span className="text-muted">{text}</span>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
